import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { faRoute, faCircleCheck, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ToHome from '../../components/common/ToHome/ToHome';
import { updateCampaignStatus } from '../../redux/actions/campaign/updateStatus';
import { setActiveMenuItem } from '../../redux/actions/menu/menu';
import { getProfileDetails, putAcknowledgementFlag } from '../../redux/actions/profile/profileDetails';
import { setSaveResultSuccess } from '../../redux/actions/saveData/saveData';
import { validateSnapshot } from '../../redux/actions/validateSnapshot/validateSnapshot';
import {
    EXTERNAL_SUBMITTED,
    RESET_UPDATE_CAMPAIGN_STATUS,
    RESET_VALIDATE_SNAPSHOT,
    UPDATE_PROPERTY
} from '../../redux/actionTypes/actionsTypes';
import { UpdateCampaignState } from '../../redux/reducers/getCampaign/updateCampaignStatusReducer';
import { FileState } from '../../redux/reducers/getFile/getFileReducer';
import { ProfileDetailsState } from '../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer';
import { PropertiesState } from '../../redux/reducers/getProperties/propertiesReducer';
import { SaveDataState } from '../../redux/reducers/getSaveData/saveDataReducer';
import { ValidateSnapshotState } from '../../redux/reducers/getSnapshot/getValidateSnapshotReducer';
import { UpdateAcknowledgementState } from '../../redux/reducers/updateAcknowledgement/updateAcknowledgementReducer';
import {
    selectGetFile,
    selectGetValidateSnapshot,
    selectMenu,
    selectProfileGetDetails,
    selectProfileGetOverview,
    selectProperties,
    selectSaveData,
    selectUpdateAcknowledgement,
    selectUpdateCampaignStatus
} from '../../redux/selectors/exportSelectorPaths';
import MenuItem from '../../types/menu/MenuItem';
import reportMenuDuration from '../../utils/GA4EventHelpers/reportMenuDuration';
import '../../components/common/ErrorMessages/ErrorMessages.css';
import './Submit.css';

export default function Submit(): JSX.Element {
    const dispatch = useDispatch();
    const { i18n } = useTranslation();
    const { t } = useTranslation(['common', 'transition', 'navigation']);

    const fileState: FileState = useSelector(selectGetFile);
    const menuState = useSelector(selectMenu);
    const profileDetails: ProfileDetailsState = useSelector(selectProfileGetDetails);
    const saveData: SaveDataState = useSelector(selectSaveData);
    const acknowledgementState: UpdateAcknowledgementState = useSelector(selectUpdateAcknowledgement);
    const updateCampaignStatusState: UpdateCampaignState = useSelector(selectUpdateCampaignStatus);
    const validateSnapshotState: ValidateSnapshotState = useSelector(selectGetValidateSnapshot);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const propertiesState: PropertiesState = useSelector(selectProperties);

    const [isCertified, setIsCertified] = useState(profileDetails.data.acknowledgementFlag || false);
    const [isCampaignUpdateReqSent, setIsCampaignUpdateReqSent] = useState(false);
    const [isValidateReqSent, setIsValidateReqSent] = useState(false);
    const isInternalUser = sessionStorage.getItem("isInternalUser")
    const isSurveyEnabled = process.env.REACT_APP_IS_SURVEY_ENABLED === "1" ? true : false

    useEffect(() => {
        if (!isInternalUser && !["GEV", "AGS"].includes(profileOverview.profile.profile_type_code)) {
            dispatch({ type: RESET_VALIDATE_SNAPSHOT })
            dispatch(validateSnapshot(sessionStorage.getItem("currentSnapshotId") as string));
            setIsValidateReqSent(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (saveData.saving) {
            dispatch(setSaveResultSuccess());
        }
    }, [dispatch, saveData.saving])

    const submit = () => {
        if (!isCertified) {
            toast.error('Please review and select the certification box before submitting your return.');
            return;
        }

        dispatch({ type: RESET_UPDATE_CAMPAIGN_STATUS });
        dispatch(updateCampaignStatus(EXTERNAL_SUBMITTED,
            sessionStorage.getItem("profileId") as string,
            sessionStorage.getItem("profileTypeCode") as string,
            sessionStorage.getItem("campaignId") as string,
            sessionStorage.getItem("rollNumber") as string,
            "false",
            sessionStorage.getItem("currentSnapshotId") as string));

        setIsCampaignUpdateReqSent(true);
    };

    useEffect(() => {
        if (!updateCampaignStatusState.loading && isCampaignUpdateReqSent) {
            if (updateCampaignStatusState.updated) {
                const campaignId = sessionStorage.getItem("campaignId")
                const statusCode = updateCampaignStatusState.campaign["statusCode"] as string
                sessionStorage.setItem("campaignStatusCode", statusCode)
                const profile = propertiesState.profiles.find(profile => String(profile.campaignId) === campaignId)
                if (profile && statusCode && profile.statusCode !== statusCode) {
                    dispatch({ type: UPDATE_PROPERTY, data: { ...profile, "statusCode": statusCode } })
                }

                // to refresh status change for landing page
            } else {
                if (updateCampaignStatusState.message) {
                    toast.error(updateCampaignStatusState.message)
                    setIsCampaignUpdateReqSent(false)
                }
            }
        }
    }, [dispatch, updateCampaignStatusState, isCampaignUpdateReqSent, propertiesState.profiles]);

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setIsCertified(isChecked);
        dispatch(putAcknowledgementFlag(
            isChecked,
            sessionStorage.getItem("currentSnapshotId") as string
        ));
    };

    useEffect(() => {
        if (acknowledgementState.success === true) {
            dispatch(getProfileDetails(sessionStorage.getItem("currentSnapshotId") as string));
        }
    }, [acknowledgementState.success, dispatch]);

    useEffect(() => {
        if (isValidateReqSent && !validateSnapshotState.loading) {
            setIsValidateReqSent(false);
        }
    }, [validateSnapshotState, isValidateReqSent])

    const findMenuItem = (pageName: string): MenuItem | null => {
        for (const item of menuState.menu) {
            if (item.name === pageName) {
                return item; // Match found in main menu
            }

            if (item.subItems) {
                const found = item.subItems.find((sub: MenuItem) => sub.name === pageName);
                if (found) return found; // Match found in submenu
            }
        }

        return null; // Not found
    }
    const navigateToPageWithError = (pageName: string) => {
        const page = findMenuItem(pageName)
        if (page) {
            dispatch(setActiveMenuItem(page));
            reportMenuDuration(menuState);
        }
    }

    return (
        <div>
            {validateSnapshotState.errorSummary.length > 0 &&
                <div className='error-container'>
                    <p className="error-heading">{t('error-msg-header')}</p>
                    <ul className='error-list'>
                        {validateSnapshotState.errorSummary.map((error, idx) => (
                            (<li key={idx} onClick={() => navigateToPageWithError(error['tab'])}>
                                <FontAwesomeIcon icon={faCircleExclamation} className="error-icon" />
                                <span>{error['errCount']} {t('submit.error-msg-1', { ns: 'transition' })} {t(error['tab'], { ns: 'navigation' })} {t('submit.error-msg-2', { ns: 'transition' })}</span>
                            </li>)

                        ))}
                    </ul>
                </div>
            }

            <div className="submit-section">
                <FontAwesomeIcon icon={faRoute} className='print-icon' />
                <p className='print-wip-text'>{t('submit-page')}</p>
                <div className="certify">
                    <input
                        type="checkbox"
                        id="certify-checkbox"
                        checked={isCertified}
                        onChange={handleCheckboxChange}
                    />
                    <label htmlFor="certify-checkbox">
                        {t('certify')}
                    </label>
                </div>
                <Button type='submit' text={t('submit')} onClick={() => submit()}
                    disabled={validateSnapshotState.checked && (validateSnapshotState.errors.length > 0 || validateSnapshotState.errorSummary?.length > 0) ? true : false} />
            </div>
            {
                (isCampaignUpdateReqSent && updateCampaignStatusState.updated) &&
                <div className="modal-bg">
                    <LoadingSpinner loading={fileState.loading}>
                        <div className="modal-container">
                            <div className="modal-body">
                                <div className='flex flex-column align-items-center'>
                                    <p><FontAwesomeIcon icon={faCircleCheck} color='green' size='3x' /></p>
                                    <p><label>{t("submit.successful", { ns: 'transition' })}</label></p>
                                    {/* <span>{t("submit.keep-ref-number-pt1", { ns: 'transition' })}&nbsp;
                                        <b>{updateCampaignStatusState.campaign.campaignId as number}</b>&nbsp;
                                        {t("submit.keep-ref-number-pt2", { ns: 'transition' })}.</span> */}
                                    <div className='button-group justify-content-center'>
                                        {(!isInternalUser && isSurveyEnabled) &&
                                            <div className='post-submit-button-container'>
                                                <div className='post-submit-button-text'>
                                                    {t("submit.survey-text", { ns: 'transition' })}
                                                </div>
                                                <ToHome preToHomeFn={() => {
                                                    const surveyLink = process.env.REACT_APP_SURVEY_LINK
                                                        + "?lang=" + i18n.language
                                                    window.open(surveyLink, "_blank", "noreferrer")
                                                }}>
                                                    <Button type="submit">
                                                        {t("submit.survey-button", { ns: 'transition' })}
                                                    </Button>
                                                </ToHome>
                                            </div>
                                        }
                                        <div className='post-submit-button-container'>
                                            <div className='post-submit-button-text'>
                                                {t("submit.to-home-text", { ns: 'transition' })}
                                            </div>
                                            <ToHome>
                                                <Button type="secondary">
                                                    {t("return", { ns: 'transition' })}
                                                </Button>
                                            </ToHome>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </LoadingSpinner>
                </div>
            }
        </div>
    );
}
